/**
 * SectionHeading Component
 * Compact uppercase section label with a leading accent dot, used to group
 * dashboard sections (Service Health, DLQ, Telemetry).
 * @packageDocumentation
 */

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Props for the SectionHeading component.
 */
export interface SectionHeadingProps {
  /** Additional CSS classes for the heading element */
  className?: string;
  /** The heading content */
  children: ReactNode;
}

/**
 * Renders a small, muted, uppercase section heading prefixed with a primary-colored dot.
 *
 * @param { SectionHeadingProps } props Component props.
 * @returns { JSX.Element } The section heading element.
 */
export const SectionHeading = ({ children, className }: SectionHeadingProps) => (
  <h2
    className={cn(
      'mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
      className,
    )}
  >
    <span className="inline-block size-1.5 rounded-full bg-primary" /> {children}
  </h2>
);
